/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useCallback, useEffect, useState } from 'react';

interface VideoProtectionOptions {
    onSuspiciousActivity?: (activity: string) => void;
    videoRef?: React.RefObject<HTMLVideoElement | null>;
    logActivity?: boolean;
}

export const useVideoProtection = (options: VideoProtectionOptions = {}) => {
    const { onSuspiciousActivity, videoRef, logActivity = true } = options;
    const [suspiciousActivities, setSuspiciousActivities] = useState<string[]>([]);
    const [isVisible, setIsVisible] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const logSuspiciousActivity = useCallback(
        (activity: string) => {
            if (logActivity) {
                console.warn('⚠️ Suspicious activity:', activity);
                setSuspiciousActivities((prev) => [...prev, `${new Date().toISOString()}: ${activity}`]);
                onSuspiciousActivity?.(activity);
            }
        },
        [logActivity, onSuspiciousActivity]
    );

    useEffect(() => {
        const handleVisibilityChange = () => {
            const visible = !document.hidden;
            setIsVisible(visible);

            if (!visible) {
                logSuspiciousActivity('کاربر تب را تغییر داد یا از مرورگر خارج شد');
                if (videoRef?.current && !videoRef.current.paused) {
                    videoRef.current.pause();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [logSuspiciousActivity, videoRef]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFS = !!(
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).msFullscreenElement
            );
            setIsFullscreen(isFS);

            if (!isFS) {
                logSuspiciousActivity('کاربر از حالت فول‌اسکرین خارج شد');
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, [logSuspiciousActivity]);

    // Monitor blur/focus events (potential screen recording app activation)
    useEffect(() => {
        let blurTime: number | null = null;

        const handleBlur = () => {
            blurTime = Date.now();
            logSuspiciousActivity('پنجره مرورگر فوکوس خود را از دست داد');
        };

        const handleFocus = () => {
            if (blurTime && Date.now() - blurTime > 2000) {
                logSuspiciousActivity('پنجره برای مدت طولانی فوکوس نداشت (احتمال استفاده از برنامه دیگر)');
            }
            blurTime = null;
        };

        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
        };
    }, [logSuspiciousActivity]);

    useEffect(() => {
        const detectDevTools = () => {
            const widthThreshold = window.outerWidth - window.innerWidth > 160;
            const heightThreshold = window.outerHeight - window.innerHeight > 160;

            if (widthThreshold || heightThreshold) {
                logSuspiciousActivity('DevTools احتمالاً باز است');
                return true;
            }
            return false;
        };

        const interval = setInterval(() => {
            detectDevTools();
        }, 3000);

        return () => clearInterval(interval);
    }, [logSuspiciousActivity]);

    useEffect(() => {
        if ('screen' in window && 'addEventListener' in (window.screen as any)) {
            const screen = window.screen as any;

            const handleCaptureChange = () => {
                if (screen.isCaptured === true) {
                    logSuspiciousActivity('ضبط صفحه‌نمایش iOS شناسایی شد');
                    if (videoRef?.current) {
                        videoRef.current.pause();
                    }
                }
            };

            screen.addEventListener('capturechange', handleCaptureChange);

            return () => {
                screen.removeEventListener('capturechange', handleCaptureChange);
            };
        }
    }, [logSuspiciousActivity, videoRef]);

    useEffect(() => {
        let screenshotAttempts = 0;
        let lastAttempt = 0;

        const handleKeyDown = (e: KeyboardEvent) => {
            const now = Date.now();

            const isPrintScreen = e.key === 'PrintScreen';
            const isMacScreenshot =
                (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) ||
                (e.metaKey && e.shiftKey && e.key.toLowerCase() === 's');
            const isWindowsScreenshot = e.key === 'Meta' && e.shiftKey && e.key.toLowerCase() === 's';

            if (isPrintScreen || isMacScreenshot || isWindowsScreenshot) {
                e.preventDefault();
                screenshotAttempts++;

                if (now - lastAttempt < 5000) {
                    logSuspiciousActivity(`تلاش برای اسکرین‌شات (${screenshotAttempts} بار)`);
                }

                lastAttempt = now;

                if (isPrintScreen && navigator.clipboard) {
                    navigator.clipboard.writeText('');
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [logSuspiciousActivity]);

    return {
        suspiciousActivities,
        isVisible,
        isFullscreen,
        clearActivities: () => setSuspiciousActivities([])
    };
};

